
import { PlanTier } from '@flagsync/skyline';
import { Loader, UserPlus } from 'lucide-react';
import { useNavigate } from 'react-router';
import { useEffect } from 'react';
import * as React from 'react';

import { NewPasswordField } from '@/components/form/new-password-field';
import { TextField } from '@/components/form/text-field';
import { StyledLink } from '@/components/styled-link';
import { Button } from '@/components/ui/button';
import { Form, FormMessage } from '@/components/ui/form';

import {
  AuthTemplate,
  FooterLinks,
} from '@/pages/unauthenticated/auth/components';
import { pricing } from '@/pages/unauthenticated/pricing/components/constants';
import { AppEnvMap } from '@/core/app-env';

import { useRegisterForm } from './hooks/use-register-form';
import { RegisterFormValues } from './form-schema';

export const RegisterForm = () => {
  const navigate = useNavigate();

  const { form, onSubmit, isLoading, isSuccess, passwordStrength } =
    useRegisterForm();

  const cfError = form.formState.errors.cfTurnstileResponse?.message;

  useEffect(() => {
    if (isSuccess) {
      navigate('/register/thank-you');
    }
  }, [isSuccess, navigate]);

  /**
   * Set the token on successful callback
   */
  useEffect(() => {
    (window as unknown as { turnstileCallback: (token: string) => void }).turnstileCallback =
      (token: string) => {
        form.setValue('cfTurnstileResponse', token, { shouldValidate: true });
      };
  }, [form]);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const plan = pricing.tiers.find((t) => t.activeTier === PlanTier.Tier320);
  if (!plan) {
    return null;
  }

  const disabled = isLoading || isSuccess;

  return (
    <>
      <AuthTemplate
        heading="Create your account"
        subheading={<RegisterSubheading />}
        footer={<FooterLinks />}
      >
        <div className="grid grid-cols-1 gap-4">
          <a
            href={`${AppEnvMap.BaseUrl}/auth/google`}
            className="flex w-full items-center justify-center gap-3 rounded-md bg-slate-200 px-3 py-2 text-sm font-semibold text-gray-900 shadow-xl ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:ring-transparent"
          >
            <svg className="h-5 w-5" aria-hidden="true" viewBox="0 0 24 24">
              <path
                d="M12.0003 4.75C13.7703 4.75 15.3553 5.36002 16.6053 6.54998L20.0303 3.125C17.9502 1.19 15.2353 0 12.0003 0C7.31028 0 3.25527 2.69 1.28027 6.60998L5.27028 9.70498C6.21525 6.86002 8.87028 4.75 12.0003 4.75Z"
                fill="#EA4335"
              />
              <path
                d="M23.49 12.275C23.49 11.49 23.415 10.73 23.3 10H12V14.51H18.47C18.18 15.99 17.34 17.25 16.08 18.1L19.945 21.1C22.2 19.01 23.49 15.92 23.49 12.275Z"
                fill="#4285F4"
              />
              <path
                d="M5.26498 14.2949C5.02498 13.5699 4.88501 12.7999 4.88501 11.9999C4.88501 11.1999 5.01998 10.4299 5.26498 9.7049L1.275 6.60986C0.46 8.22986 0 10.0599 0 11.9999C0 13.9399 0.46 15.7699 1.28 17.3899L5.26498 14.2949Z"
                fill="#FBBC05"
              />
              <path
                d="M12.0004 24.0001C15.2404 24.0001 17.9654 22.935 19.9454 21.095L16.0804 18.095C15.0054 18.82 13.6204 19.245 12.0004 19.245C8.8704 19.245 6.21537 17.135 5.2654 14.29L1.27539 17.385C3.25539 21.31 7.3104 24.0001 12.0004 24.0001Z"
                fill="#34A853"
              />
            </svg>
            <span className="text-sm font-semibold leading-6">
              Continue with Google
            </span>
          </a>
        </div>
        <div className="flex items-center py-1">
          <div className="grow border-t border-border"></div>
          <span className="mx-4 text-sm font-medium leading-6 text-muted-foreground">
            OR
          </span>
          <div className="grow border-t border-border"></div>
        </div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit, console.error)}
            className="space-y-4"
          >
            <div className="flex flex-wrap gap-2">
              <TextField<RegisterFormValues>
                form={form}
                disabled={disabled}
                name="firstName"
                label="First name"
                autoComplete="firstName"
                formItemClassName="flex-1 min-w-[200px]"
              />

              <TextField<RegisterFormValues>
                form={form}
                disabled={disabled}
                name="lastName"
                label="Last name"
                autoComplete="lastName"
                formItemClassName="flex-1 min-w-[200px]"
              />
            </div>

            <TextField<RegisterFormValues>
              form={form}
              disabled={disabled}
              name="email"
              label="Email"
              autoComplete="email"
            />

            <NewPasswordField<RegisterFormValues>
              form={form}
              disabled={disabled}
              name="password"
              label="Password"
              autoComplete="new-password"
              passwordStrength={passwordStrength}
            />

            <TextField<RegisterFormValues>
              form={form}
              disabled={disabled}
              name="organizationName"
              label="Company name"
              description="Don't worry, you can change this once you're in the app."
            />

            <Button disabled={disabled} className="w-full" type="submit">
              {disabled ? (
                <Loader className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <UserPlus className="mr-2 h-4 w-4" />
              )}
              Create Account
            </Button>

            {cfError && (
              <FormMessage className="font-semibold flex flex-col gap-y-2.5">
                <span>{cfError}</span>
                <span>
                  If the problem persists, please contact{' '}
                  <StyledLink
                    className="text-destructive underline hover:text-destructive/80"
                    href="mailto:support@flagsync.com"
                  >
                    support@flagsync.com
                  </StyledLink>
                  .
                </span>
              </FormMessage>
            )}

            <div
              data-size="flexible"
              data-theme="dark"
              className="cf-turnstile"
              data-sitekey={AppEnvMap.CfSiteKey}
              data-callback="turnstileCallback"
            />
          </form>
        </Form>
      </AuthTemplate>
    </>
  );
};

function RegisterSubheading() {
  return (
    <span>
      Already a member?{' '}
      <StyledLink href="/login" className="font-medium">
        Sign in
      </StyledLink>
    </span>
  );
}
